import type {
  WikiAgentReplyOutcome,
  WikiAgentReplyView,
  WikiComment,
  WikiThreadAgentState,
} from "@esi/shared";
import { Bot, Clock, GitCompareArrows, Lock } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { formatDateTime } from "../i18n/format.ts";
import { useSession } from "../session.tsx";

// The agent's replies in a review thread (wiki-resolve-with-agent D6, D7, D10): under the comment
// they answer, in the dashed suggestion anatomy; a question's choices as outline buttons; a reply
// the viewer may not read as one placeholder line with no outcome, text or choice.

/** The outcome as a chip: always written, the tint only supports it (Changed, Question, No change). */
export function OutcomeChip(props: {
  outcome: WikiAgentReplyOutcome;
  /** Who a question is for: "Question for Bob", or "Question for you" when `you`. */
  name?: string;
  you?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <span
      data-outcome={props.outcome}
      className={cn(
        "inline-flex items-center rounded-[5px] px-1.5 py-px text-[11px] font-semibold",
        props.outcome === "changed" && "bg-success-bg text-success-fg",
        props.outcome === "question" && "bg-info-bg text-info-fg",
        props.outcome === "no_change" && "bg-surface-selected text-fg-secondary",
      )}
    >
      {props.outcome === "question"
        ? props.you
          ? t("wiki.resolve.reply.outcome.questionYou")
          : t("wiki.resolve.reply.outcome.question", {
              name: props.name ?? t("wiki.resolve.reply.someone"),
            })
        : t(`wiki.resolve.reply.outcome.${props.outcome}`)}
    </span>
  );
}

type Entry = { comment: WikiComment } | { reply: WikiAgentReplyView };

/**
 * The thread's comments with its agent replies placed after the last comment written before each
 * reply (sent ones by their sending time, the creator's drafts by their drafting time).
 */
export function withAgentReplies(
  comments: readonly WikiComment[],
  state: WikiThreadAgentState | undefined,
): Entry[] {
  const at = (r: WikiAgentReplyView) => r.sentAt ?? ("createdAt" in r ? r.createdAt : "") ?? "";
  const replies = [...(state?.replies ?? [])].sort((a, b) => at(a).localeCompare(at(b)));
  const out: Entry[] = [];
  let next = 0;
  for (const comment of comments) {
    while (next < replies.length && at(replies[next] as WikiAgentReplyView) < comment.createdAt) {
      out.push({ reply: replies[next] as WikiAgentReplyView });
      next++;
    }
    out.push({ comment });
  }
  for (; next < replies.length; next++) {
    out.push({ reply: replies[next] as WikiAgentReplyView });
  }
  return out;
}

/** One agent reply (D7): "The agent, for Alice", the outcome, the text, See the change, choices. */
export function AgentReply(props: {
  reply: WikiAgentReplyView;
  /** The thread's author, whom a question asks. */
  reviewer: { userId: string; name: string | null };
  /** The thread waits on its reviewer: the latest sent question nobody but the creator answered. */
  waiting: WikiThreadAgentState["waitingFor"] | undefined;
  /** The viewer may comment: the choices post their text as a comment. */
  mayReply: boolean;
  busy: boolean;
  onPick: (choice: string) => void;
  onAnswer: () => void;
  /** Opens the passage's change (a Changed reply that touched the thread's item). */
  onSeeChange: (() => void) | null;
}) {
  const { t } = useTranslation();
  const { userId } = useSession();
  const { reply } = props;
  const forName = reply.forName
    ? t("wiki.resolve.reply.forName", { name: reply.forName })
    : t("wiki.resolve.reply.forSomeone");
  const you = props.reviewer.userId.toLowerCase() === userId.toLowerCase();
  if (reply.hidden) {
    // D10: no outcome, text or choice; the waited-on reviewer asks the creator instead.
    return (
      <li
        data-agent-reply="hidden"
        className="flex items-start gap-2 rounded-xl border border-dashed border-line px-3 py-2 text-[13px] text-fg-muted"
      >
        <Lock aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
        <span>
          {props.waiting?.hiddenFromThem && reply.forName
            ? t("wiki.resolve.reply.hiddenAsk", { name: reply.forName })
            : t("wiki.resolve.reply.hidden")}
        </span>
      </li>
    );
  }
  const draft = reply.status === "draft";
  const asks =
    reply.outcome === "question" &&
    !draft &&
    props.waiting !== undefined &&
    !props.waiting.hiddenFromThem;
  // The choices answer for the reviewer it asks; anyone else replies in their own words.
  const asked = asks && props.waiting?.userId.toLowerCase() === userId.toLowerCase();
  return (
    <li
      data-agent-reply={reply.outcome}
      data-esi-content="wiki"
      aria-label={t("wiki.resolve.reply.label")}
      className="flex flex-col gap-1.5 rounded-xl border border-dashed border-line-strong bg-surface px-3 py-2.5"
    >
      <p className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[13px] font-medium text-fg">
        <Bot aria-hidden="true" className="size-3.5 shrink-0 text-fg-muted" />
        <span className="break-words">{forName}</span>
        <OutcomeChip
          outcome={reply.outcome}
          name={props.reviewer.name ?? t("wiki.resolve.reply.someone")}
          you={you}
        />
        {draft ? (
          <span className="text-xs font-normal text-fg-muted">{t("wiki.resolve.reply.draft")}</span>
        ) : null}
      </p>
      <p className="text-sm whitespace-pre-wrap break-words text-fg">{reply.text}</p>
      {asked && reply.choices.length && props.mayReply ? (
        <fieldset className="m-0 flex min-w-0 flex-col gap-1.5 border-0 p-0 pt-0.5">
          <legend className="sr-only">{t("wiki.resolve.reply.choices")}</legend>
          {reply.choices.map((choice) => (
            <Button
              key={choice}
              type="button"
              variant="outline"
              size="sm"
              disabled={props.busy}
              className="h-auto min-h-8 justify-start py-1.5 text-left whitespace-normal"
              onClick={() => props.onPick(choice)}
            >
              {choice}
            </Button>
          ))}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={props.busy}
            className="justify-start text-fg-secondary"
            onClick={props.onAnswer}
          >
            {t("wiki.resolve.reply.answer")}
          </Button>
        </fieldset>
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        {reply.outcome === "changed" && props.onSeeChange ? (
          <Button
            type="button"
            size="xs"
            variant="ghost"
            className="-ml-2 text-fg-secondary"
            onClick={props.onSeeChange}
          >
            <GitCompareArrows aria-hidden="true" />
            {t("wiki.resolve.reply.seeChange")}
          </Button>
        ) : null}
        {reply.sentAt ? (
          <time className="text-xs text-fg-muted" dateTime={reply.sentAt}>
            {formatDateTime(reply.sentAt)}
          </time>
        ) : null}
      </div>
      {asks ? (
        <p className="flex items-center gap-1.5 text-xs text-info-fg">
          <Clock aria-hidden="true" className="size-3.5 shrink-0" />
          {you && reply.forName
            ? t("wiki.resolve.reply.waitingYou", { name: reply.forName })
            : t("wiki.resolve.reply.waiting", {
                name: props.waiting?.name ?? t("wiki.resolve.reply.someone"),
              })}
        </p>
      ) : null}
    </li>
  );
}

/**
 * A thread's comments and agent replies in order, each comment rendered by `comment` (the
 * caller's own comment view), each reply as `AgentReply`.
 */
export function ThreadEntries(props: {
  comments: readonly WikiComment[];
  agentReplies: WikiThreadAgentState | undefined;
  reviewer: { userId: string; name: string | null };
  mayReply: boolean;
  busy: boolean;
  onPick: (choice: string) => void;
  onAnswer: () => void;
  onSeeChange: ((reply: WikiAgentReplyView) => void) | null;
  comment: (comment: WikiComment) => ReactNode;
  className?: string;
}) {
  const entries = withAgentReplies(props.comments, props.agentReplies);
  // The thread waits on its latest sent reply only (D6): that one, visible or hidden, carries the
  // wait; an older question or hidden reply never does.
  const waitingOn = props.agentReplies?.waitingFor;
  const latestSent = [...entries]
    .reverse()
    .find((e): e is { reply: WikiAgentReplyView } => "reply" in e && e.reply.sentAt !== null)
    ?.reply.id;
  return (
    <ol className={props.className ?? "flex flex-col gap-3"}>
      {entries.map((entry) =>
        "comment" in entry ? (
          props.comment(entry.comment)
        ) : (
          <AgentReply
            key={entry.reply.id}
            reply={entry.reply}
            reviewer={props.reviewer}
            waiting={entry.reply.id === latestSent ? waitingOn : undefined}
            mayReply={props.mayReply}
            busy={props.busy}
            onPick={props.onPick}
            onAnswer={props.onAnswer}
            onSeeChange={
              props.onSeeChange && !entry.reply.hidden && entry.reply.changeOf
                ? () => props.onSeeChange?.(entry.reply)
                : null
            }
          />
        ),
      )}
    </ol>
  );
}
